import { DateTime } from "luxon";
export default {
    data() {
        return {
            moveHours: 6,
            datetimes: [],
            currentTime: null,
            referenceTime: null
        };
    },
    computed: {
        minuteLineStyle() {
            // Position of line based on currentMinute
            const percent = (this.currentTime.minute / 60) * 100;
            return {
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${percent}%`,
                borderLeft: "2px solid red",
                zIndex: 1
            };
        },
        currentLineTooltip() {
            let hour = Number(this.currentTime.hour);
            if (this.currentTime.meridiem == "PM") {
                hour = hour - 12;
            }
            const h = String(hour).padStart(2, "0");
            const m = String(this.currentTime.minute).padStart(2, "0");
            return `${h}:${m} ${this.currentTime.meridiem}`;
        }
    },
    methods: {
        TreatmentStatus(treatment) {
            const activeStatus = treatment.treatment_status.find(
                status => status.isactive === true
            );
            return activeStatus;
        },
        setToSelectedDate(date) {
            const convertedDate = this._fromDateStrToDateTime(date);
            this.currentTime.reference = convertedDate;
            this.generateTimeSlots();
        },
        setToCurrentTime(resetLocation = true) {
            this.computeFilteredTreatments++;

            // Get the current date and time in the specified time zone
            const localTime = DateTime.now().setZone(this._timeZone);
            this.currentTime = this.prepareDateTime(localTime, resetLocation);

            // Calculate the milliseconds until the next minute
            const millisecondsUntilNextMinute =
                (60 - localTime.second) * 1000 - localTime.millisecond;

            // Schedule the next update
            this.timeTimeout = setTimeout(() => {
                this.setToCurrentTime(false);
            }, millisecondsUntilNextMinute);

            this.generateTimeSlots();
        },
        prepareDateTime(selectedDate, resetLocation = true) {
            const hour12 = selectedDate.toFormat("h");
            const meridiem = selectedDate.toFormat("a");
            const hour = selectedDate.hour;
            const minute = selectedDate.minute;
            const minuteString = minute.toString().padStart(2, "0");

            const time = `${hour12}:${minuteString}`;
            const timeSimplify = parseInt(hour12);

            // Set the reference time to 12 AM or 12 PM based on the current meridiem
            const referenceTime = resetLocation
                ? meridiem === "AM"
                    ? selectedDate.startOf("day").set({ hour: 0 })
                    : selectedDate.startOf("day").set({ hour: 12 })
                : this.currentTime.reference;

            return {
                time: time,
                timeSimplify: timeSimplify,
                hour: hour,
                minute: minute,
                meridiem: meridiem,
                date: selectedDate.toFormat(this._dateFormat),
                current: selectedDate,
                reference: referenceTime
            };
        },
        generateTimeSlots() {
            this.datetimes = [];

            const getDate = this.currentTime;
            for (let i = 0; i < 12; i++) {
                // Calculate the time slot by adding 'i' hours to the referenceTime
                let timeSlot = getDate.reference.plus({ hours: i });

                // Extract hour and meridiem
                let hour = parseInt(timeSlot.toFormat("h"));
                let meridiem = timeSlot.toFormat("a");

                // Check if this time slot matches the current time
                const isCurrentTime =
                    hour === parseInt(getDate.current.toFormat("h")) &&
                    meridiem === getDate.current.toFormat("a") &&
                    timeSlot.toFormat(this._dateFormat) == getDate.date;

                // Create the time slot object
                let detailHour = {
                    hour: hour,
                    meridiem: meridiem,
                    date: timeSlot.toFormat(this._dateFormat),
                    dateSimplify: timeSlot.toFormat("dd LLL"),
                    oriDate: timeSlot,
                    isCurrentTime: isCurrentTime // Indicator for the current time
                };
                this.datetimes.push(detailHour);
            }
        },
        onNavigateButtonClick(next = true) {
            this.currentTime.reference = next
                ? this.currentTime.reference.plus({
                      hours: this.moveHours
                  })
                : this.currentTime.reference.minus({
                      hours: this.moveHours
                  });
            this.generateTimeSlots();
        },
        getDueForActivity(treatment) {
            const activities = treatment.activities;
            const status = this.TreatmentStatus(treatment);
            const isOverAdmitted =
                status && status.status_id > this.TREATMENT_STATUS.ADMITTED;

            // Prepare arrays for segregation
            let overdueActivities = [];
            let activeActivities = [];
            let completedActivities = [];

            // Single pass over activities
            let currTime = this._now();
            for (let activity of activities) {
                // Clone and prepare a final object
                let finalObj = { ...activity };

                // We'll need two separate "due" calculations
                // 1) For pending (active/overdue) scenario
                const pendingDueCalculated = this._calculateDueAlt(
                    DateTime.fromSeconds(treatment.start),
                    activity.due,
                    activity.due_type,
                    currTime
                );

                // 2) For completed scenario
                const completedDueCalculated = this._calculateDueAlt(
                    this._fromDateCarbon(
                        treatment.treatment_status[0].create_at
                    ),
                    activity.due,
                    activity.due_type,
                    currTime
                );

                // Figure out if this activity is considered "completed" in the original logic
                // Original filter logic:
                //   - activity.complete_at is defined, or
                //   - if (status_id > ADMITTED), activity.status is STARTED or DEACTIVATED, or
                //   - if (status_id <= ADMITTED), the original code uses the `: true` branch,
                //     so isactive activities all end up in "completed" as well.
                let isCompleted = false;

                if (activity.complete_at) {
                    isCompleted = true;
                } else if (isOverAdmitted) {
                    // If status > ADMITTED and activity is STARTED or DEACTIVATED, original code lumps it under completed
                    if (
                        activity.status ===
                            this.TREATMENT_ACTIVITY_STATUS.STARTED ||
                        activity.status ===
                            this.TREATMENT_ACTIVITY_STATUS.DEACTIVATED
                    ) {
                        isCompleted = true;
                    }
                } else {
                    // If we are not over ADMITTED, the filter says `: true`,
                    // so effectively we consider all isactive as well.
                    isCompleted = true;
                }

                // Now decide which bucket to place it in
                if (isCompleted && activity.isactive) {
                    // "Completed" path
                    let referTime;
                    if (activity.complete_at) {
                        // If there's an explicit complete_at, use that as referTime
                        referTime = activity.complete_at;
                    } else {
                        // Otherwise, we mimic original logic: use update_at as seconds
                        let tmp = this._fromDateCarbon(
                            activity.update_at
                        ).setZone(this._timeZone);
                        referTime = tmp.toSeconds();
                    }

                    finalObj.dueCalculated = completedDueCalculated;
                    finalObj.generalStatus = "completed";
                    finalObj.referTime = referTime;

                    // Keep "actualStatus" logic the same:
                    if (!activity.complete_at) {
                        finalObj.actualStatus =
                            activity.status ===
                            this.TREATMENT_ACTIVITY_STATUS.DEACTIVATED
                                ? "deactivated"
                                : "incomplete";
                    }

                    completedActivities.push(finalObj);
                }
                // else check if it can be "active" or "overdue"
                else if (
                    isOverAdmitted &&
                    activity.isactive &&
                    activity.status === this.TREATMENT_ACTIVITY_STATUS.PENDING
                ) {
                    // In original code, only pending & isactive & status > ADMITTED are considered
                    // for "overdue" or "active"
                    finalObj.dueCalculated = pendingDueCalculated;
                    const time = finalObj.dueCalculated?.time || 0;

                    if (finalObj.dueCalculated.text === "Overdue") {
                        finalObj.generalStatus = "overdue";
                        finalObj.referTime = time;
                        overdueActivities.push(finalObj);
                    } else {
                        finalObj.generalStatus = "active";
                        finalObj.referTime = time;
                        activeActivities.push(finalObj);
                    }
                }
                // else: it doesn't fit the overdue/active/complete criteria, so skip
            }

            // Combine and sort by referTime
            treatment.activities = [
                ...completedActivities,
                ...activeActivities,
                ...overdueActivities
            ].sort((a, b) => a.referTime - b.referTime);

            // Also provide the segregated version
            treatment.segregatedActivities = {
                active: activeActivities,
                overdue: overdueActivities,
                completed: completedActivities
            };

            return treatment;
        },
        prepareActivities(treatment, split = false) {
            const activities = treatment.activities;
            const status = this.TreatmentStatus(treatment);

            // to find started activities that are overdues or still active
            let overdueActivities = [];
            let activeActivities = [];
            if (status && status.status_id > this.TREATMENT_STATUS.ADMITTED) {
                let activitiesFilter = treatment.activities
                    .map(activity => ({
                        ...activity,
                        dueCalculated: this._calculateDueAlt(
                            DateTime.fromSeconds(treatment.start),
                            activity.due,
                            activity.due_type,
                            this._now()
                        )
                    }))
                    .filter(
                        activity =>
                            activity.isactive &&
                            activity.status ===
                                this.TREATMENT_ACTIVITY_STATUS.PENDING
                    )
                    .sort((a, b) => {
                        const timeA = a.dueCalculated?.time || 0;
                        const timeB = b.dueCalculated?.time || 0;
                        return timeA - timeB;
                    });

                let allActivities = activitiesFilter.filter(
                    activity =>
                        activity.isactive === true &&
                        activity.status ===
                            this.TREATMENT_ACTIVITY_STATUS.PENDING
                );

                overdueActivities = allActivities
                    .filter(
                        activity => activity.dueCalculated.text === "Overdue"
                    )
                    .map(activity => ({
                        ...activity,
                        generalStatus: "overdue",
                        referTime: activity.dueCalculated.time
                    }));

                activeActivities = allActivities
                    .filter(
                        activity => activity.dueCalculated.text !== "Overdue"
                    )
                    .map(activity => ({
                        ...activity,
                        generalStatus: "active",
                        referTime: activity.dueCalculated.time
                    }));
            }

            const completedActivities = activities.map(activity => ({
                ...activity,
                dueCalculated: this._calculateDueAlt(
                    this._fromDateCarbon(
                        treatment.treatment_status[0].create_at
                    ),
                    activity.due,
                    activity.due_type,
                    this._now()
                )
            }));
            const filteredCompletedActivities = completedActivities
                .filter(
                    activity =>
                        activity.isactive &&
                        (status.status_id > this.TREATMENT_STATUS.ADMITTED
                            ? activity.status ===
                                  this.TREATMENT_ACTIVITY_STATUS.STARTED ||
                              activity.status ===
                                  this.TREATMENT_ACTIVITY_STATUS.DEACTIVATED
                            : true)
                )
                .map(activity => {
                    if (activity.complete_at) {
                        return {
                            ...activity,
                            generalStatus: "completed",
                            referTime: activity.complete_at
                        };
                    } else {
                        let referTime = this._fromDateCarbon(
                            activity.update_at
                        ).setZone(this._timeZone);
                        referTime = referTime.toSeconds();
                        return {
                            ...activity,
                            generalStatus: "completed",
                            referTime: referTime,
                            actualStatus:
                                activity.status ==
                                this.TREATMENT_ACTIVITY_STATUS.DEACTIVATED
                                    ? "deactivated"
                                    : "incomplete"
                        };
                    }
                });

            if (split) {
                return {
                    completed: filteredCompletedActivities,
                    active: activeActivities,
                    overdue: overdueActivities
                };
            } else {
                return [
                    ...filteredCompletedActivities,
                    ...activeActivities,
                    ...overdueActivities
                ].sort((a, b) => a.referTime - b.referTime);
            }
        }
    }
};
